<script setup lang="ts">
import BackgroundBanner from '~/components/effects/BackgroundBanner.vue'
import CardDetailResult from '~/components/pages/results/tabs/results/CardDetailResultNew.vue'
import Pagination from '~/components/pages/results/Pagination.vue'
import LoaderResults from '~/components/pages/results/LoaderResults.vue'
import { fetchLotterySelect } from '~/utils/playLottery.js'
import { useDetailResultStore } from '~/store/detail_result'
import { useResultStore } from '~/store/result'
import { filteredQueryParams } from '~/utils/filteredQueryParams'
import { formatCurrency } from '~/utils/formatCurrency'
import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import { toQueryString } from '~/utils/queryString'
import { ID_KENO_FR, ID_EURO_MILLIONS } from '~/utils/constants/idsLotteries'

const resultStore = useResultStore()
const detailResultStore = useDetailResultStore()
const { t: $t, locale } = useI18n()

defineOptions({
	name: 'ModalDetail',
})

interface Pagination {
	current: number
	per_page: number
	total_pages: number
}

interface NumbersBlock {
	first: (string | null)[]
	last: (string | null)[] | any
	prize: (string | null)[] | any
}

interface RowBlock {
	prize_id: number
	numbers: NumbersBlock
	prize_group?: {
		extra: number
		regular: number
	}
}

interface QueryParam {
	lottery_id: number
	first_number: number
	second_number: number
	third_number: number
	fourth_number: number
	fifth_number: number
	page_number: number
	prize_id: number
	first_number_filter?: number
	second_number_filter?: number
	third_number_filter?: number
	fourth_number_filter?: number
	fifth_number_filter?: number
}

interface Params {
	lottery_id: null
	page_number: number
	regular_balls: number[]
	regular_ball_quantity: number | undefined
	extra_ball_quantity?: number | undefined
	extra_balls?: any
	number_of_balls?: number
}

interface Lottery {
	id: number
	name: string
	slug: string
	image: string
}

const initialBlock = {
	first: [],
	last: [],
	prize: null,
}
const initialText = {
	title: 'Analyzing History',
	description: 'Searching through all recorded years',
}
const loadingText = ref({
	title: $t(initialText.title),
	description: `${$t(initialText.description)}...`,
})

let timeoutId: ReturnType<typeof setTimeout> | null = null

const pagination = computed<Pagination | null>(() => {
	return detailResultStore.pagination
})

const getLotteries = computed(() => {
	if (detailResultStore.lotteries === null) return []
	return detailResultStore.lotteries
})

const getLottery = computed<Lottery | null>(() => {
	if (resultStore.lottery_id === null) return null
	return fetchLotterySelect(resultStore.lottery_id)
})

const close = () => {
	resultStore.SET_STATE_RESULT({
		type: 'showModalDetail',
		value: false,
	})
	detailResultStore.SET_STATE_DETAIL_RESULT({
		type: 'current_page',
		value: 1,
	})
}

const getQuery = (position: number) => {
	if (!resultStore.lotteries) return
	const row: RowBlock = resultStore.lotteries[position]
	const numbers: NumbersBlock = resultStore.numbers ?? initialBlock

	if (resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id)) {
		const [first_number, second_number, third_number, fourth_number, fifth_number] = numbers.first?.map((num: any) =>
			Number(num),
		)

		const [first_number_filter, second_number_filter, third_number_filter, fourth_number_filter, fifth_number_filter] =
			row?.numbers.prize
		const query: QueryParam = {
			lottery_id: resultStore.lottery_id,
			first_number,
			second_number,
			third_number,
			fourth_number,
			fifth_number,
			page_number: detailResultStore.current_page,
			prize_id: row?.prize_id,
		}
		if (first_number_filter.number !== null) {
			query.first_number_filter = first_number_filter.number
		}
		if (second_number_filter.number !== null) {
			query.second_number_filter = second_number_filter.number
		}
		if (third_number_filter.number !== null) {
			query.third_number_filter = third_number_filter.number
		}
		if (fourth_number_filter.number !== null) {
			query.fourth_number_filter = fourth_number_filter.number
		}
		if (fifth_number_filter.number !== null) {
			query.fifth_number_filter = fifth_number_filter.number
		}

		return query
	}
	const params: Params = {
		lottery_id: resultStore.lottery_id,
		page_number: detailResultStore.current_page,
		regular_balls: numbers && numbers?.first ? numbers.first.map((num) => Number(num)) : [],
		regular_ball_quantity: row.prize_group?.regular,
	}
	if (row.prize_group?.extra && row.prize_group?.extra !== null) {
		params.extra_ball_quantity = row.prize_group?.extra
	}
	if (ID_KENO_FR === resultStore.lottery_id) {
		params.number_of_balls = numbers.first.length
	}
	if (row.numbers?.last?.length > 0) {
		params.extra_balls = numbers.last.map((num: string) => Number(num))
	}

	return params
}

const changePage = async (page: number) => {
	detailResultStore.SET_STATE_DETAIL_RESULT({
		type: 'current_page',
		value: page,
	})

	const params =
		detailResultStore.select_position === null
			? null
			: toQueryString(filteredQueryParams(getQuery(detailResultStore.select_position)))
	if (params === null) return
	if (resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id)) {
		await detailResultStore.fetchDetailResultSaturday(params, locale.value)
		return
	}

	await detailResultStore.fetchDetailResult(params, locale.value)
}

watch(
	() => detailResultStore.loading,
	(isLoading) => {
		if (isLoading) {
			loadingText.value = {
				title: $t(initialText.title),
				description: `${$t(initialText.description)}...`,
			}

			timeoutId = setTimeout(() => {
				if (detailResultStore.loading) {
					loadingText.value = {
						title: $t('Preparing Results'),
						description: `${$t('Organizing all the results')}...`,
					}
				}
			}, 2500)
		} else {
			if (timeoutId) {
				clearTimeout(timeoutId)
				timeoutId = null
			}
		}
	},
	{ immediate: true },
)
</script>

<template>
	<ClientOnly>
		<UModal
			v-model="resultStore.showModalDetail"
			class="modal-contact"
			:ui="{
				overlay: {
					background: 'bg-[#040404D4] bg-opacity-80',
				},
				width: 'w-full sm:max-w-3xl xl:max-w-5xl',
				container: 'items-center sm:items-start',
				padding: 'p-6 sm:p-8',
				rounded: 'rounded-lg sm:rounded-3xl',
			}"
		>
			<div class="bg-secondary-700 rounded-lg sm:rounded-3xl">
				<div class="w-full relative z-10 py-5 sm:py-10 px-4 smm:px-8 sm:px-14">
					<BackgroundBanner />
					<div class="flex justify-end">
						<UButton
							color="white"
							variant="link"
							size="xl"
							icon="i-heroicons-x-mark-20-solid"
							class="text-neutral-50"
							@click="close"
						/>
					</div>
					<div class="w-full space-y-9">
						<div class="space-y-12">
							<div class="space-y-4 text-neutral-50 text-center smm:text-left">
								<h2 class="font-semibold text-lg sm:text-3xl">{{ $t('Results History') }}</h2>
								<p class="text-sm sm:text-base">
									{{ $t('See the date and amount won each time by this number combination') }}.
								</p>
							</div>
							<div class="flex flex-col md:flex-row gap-4">
								<div
									class="rounded-2xl bg-secondary-800 sm:bg-secondary-700 border border-secondary-700 min-h-24 w-full md:max-w-72 flex flex-row justify-center gap-5 px-4 py-5"
									:class="[
										{
											'md:max-w-80 flex-col smm:flex-row':
												resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id),
										},
										resultStore.lottery_id && IDS_LOTTERIES_CLASSIC.includes(resultStore.lottery_id)
											? 'items-start'
											: 'items-center',
									]"
								>
									<div class="bg-green-500 bg-opacity-20 text-primary-400 px-5 py-8 rounded-2xl">
										<UIcon
											name="i-hugeicons:coins-01"
											class="size-10"
										/>
									</div>
									<div class="text-left space-y-3 text-neutral-50">
										<p class="font-semibold text-lg">
											{{ getLottery?.id === ID_EURO_MILLIONS ? $t(getLottery.name) : getLottery?.name }}
										</p>
										<div class="space-0.5">
											<p class="text-sm">{{ $t('Total Prize Amount') }}</p>
											<p
												v-if="detailResultStore.detail"
												class="font-semibold leading-7 text-lg"
											>
												{{ formatCurrency(detailResultStore.detail?.jackpot, detailResultStore.detail?.type_currency) }}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
						<LoaderResults
							v-if="detailResultStore.loading"
							:show-custom-message="true"
							:title="$t(loadingText.title)"
							:description="$t(loadingText.description)"
						/>
						<template v-else>
							<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-9">
								<div
									v-for="(lottery, index) in getLotteries"
									:key="index"
								>
									<CardDetailResult
										:lottery="lottery"
										:show-btn-detail="false"
									/>
								</div>
							</div>
							<div
								v-if="pagination && pagination?.total_pages > 1"
								class="flex justify-center"
							>
								<Pagination
									:total="pagination?.total_pages"
									:page-count="pagination?.per_page"
									:current="pagination?.current"
									@change="changePage"
								/>
							</div>
						</template>
					</div>
				</div>
			</div>
		</UModal>
	</ClientOnly>
</template>
